'use client';

import { useState } from 'react';
import { usePublicUi } from '@/lib/publicUi';
import styles from './ShareReportDialog.module.css';

const REASON_KEYS = [
  ['spam', 'shareReasonSpam'],
  ['abuse', 'shareReasonAbuse'],
  ['illegal', 'shareReasonIllegal'],
  ['copyright', 'shareReasonCopyright'],
  ['privacy', 'shareReasonPrivacy'],
  ['misleading', 'shareReasonMisleading'],
  ['other', 'shareReasonOther'],
] as const;

export function currentIdToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    (window as Window & { __CONTAIND_ID_TOKEN__?: string | null })
      .__CONTAIND_ID_TOKEN__ ?? null
  );
}

export function ShareReportDialog({
  shareId,
  slug,
  title,
  onClose,
}: {
  shareId: string;
  slug: string | null;
  title: string;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<(typeof REASON_KEYS)[number][0]>('spam');
  const [custom, setCustom] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const { locale, extra } = usePublicUi();

  const submit = () => {
    void (async () => {
      setBusy(true);
      setError(null);
      const token = currentIdToken();
      if (!token) {
        onClose();
        return;
      }
      if (reason === 'other' && custom.trim().length < 2) {
        setError(extra.shareCustomReasonError);
        setBusy(false);
        return;
      }
      if (note.trim().length < 4) {
        setError(extra.shareDetailsError);
        setBusy(false);
        return;
      }
      const apiBase = (
        process.env.NEXT_PUBLIC_PUBLIC_SHARE_API_BASE ||
        'https://api.containd.app/v1'
      ).replace(/\/$/, '');
      try {
        const res = await fetch(`${apiBase}/publicShareReport`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            shareId,
            slug,
            reasonCode: reason,
            customReason: reason === 'other' ? custom.trim() : undefined,
            reasoning: note.trim(),
          }),
        });
        if (res.status === 401) {
          const next = encodeURIComponent(
            window.location.pathname + window.location.search,
          );
          window.location.href = `/${locale}/login/?next=${next}&intent=report`;
          return;
        }
        if (res.status === 429) {
          setError(extra.shareAlreadyReported);
          return;
        }
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          setError(data.error || extra.shareReportFailed);
          return;
        }
        setSent(true);
        setNote('');
        setCustom('');
      } catch {
        setError(extra.shareReportFailed);
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={onClose}
    >
      <div
        className={styles.card}
        role="dialog"
        aria-labelledby="report-title"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="report-title">{extra.shareReportTitle}</h2>
        {sent ? (
          <>
            <p>
              {extra.shareReportThanks} “{title}”.
            </p>
            <button type="button" className={styles.primary} onClick={onClose}>
              {extra.close}
            </button>
          </>
        ) : (
          <>
            <p className={styles.hint}>{extra.shareReportHint}</p>
            <fieldset className={styles.reasons}>
              <legend>{extra.shareReasonLegend}</legend>
              {REASON_KEYS.map(([id, key]) => (
                <label key={id} className={styles.reason}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={id}
                    checked={reason === id}
                    onChange={() => setReason(id)}
                  />
                  <span>{extra[key]}</span>
                </label>
              ))}
            </fieldset>
            {reason === 'other' ? (
              <input
                className={styles.input}
                type="text"
                maxLength={200}
                placeholder={extra.shareCustomReasonPh}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
              />
            ) : null}
            <textarea
              className={styles.input}
              rows={4}
              placeholder={extra.shareDetailsPh}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {error ? (
              <p className={styles.hint} role="alert">
                {error}
              </p>
            ) : null}
            <div className={styles.actions}>
              <button type="button" onClick={onClose}>
                {extra.cancel}
              </button>
              <button
                type="button"
                className={styles.primary}
                disabled={busy}
                onClick={submit}
              >
                {busy ? extra.shareSending : extra.shareSubmitReport}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
